import React, { useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';
import Chart from 'chart.js';
import { jsPDF } from 'jspdf';
import { HiDownload } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';


const EXPORT_SCALE = 3;
const PDF_MARGIN = 24; // pt
const PDF_IMAGE_QUALITY = 0.92;

const renderChartsAt = (charts, ratios) => {
  charts.forEach((chart, index) => {
    chart.options.devicePixelRatio = ratios[index];
    chart.width = 0;
    chart.resize(true);
    chart.draw();
  });
};

const boostChartResolution = (node) => {
  const charts = Object.values(Chart.instances || {}).filter(
    (chart) => chart.canvas && node.contains(chart.canvas)
  );
  const originalRatios = charts.map((chart) => chart.options.devicePixelRatio);
  renderChartsAt(charts, charts.map(() => EXPORT_SCALE));
  return () => renderChartsAt(charts, originalRatios);
};

const DashboardDownloadButton = ({ downloadRef, reportName = 'Report', disabled = false }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const captureCanvas = async () => {
    const doc = document.getElementById(downloadRef.current.id);
    const controls = doc.querySelector('.no-export');
    if (controls) controls.style.display = 'none';
    
    const expandables = Array.from(doc.querySelectorAll('[data-export-expand]'));
    const savedStyles = expandables.map((el) => ({
      height: el.style.height,
      maxHeight: el.style.maxHeight,
      overflowY: el.style.overflowY,
    }));
    expandables.forEach((el) => {
      el.style.height = 'auto';
      el.style.maxHeight = 'none';
      el.style.overflowY = 'visible';
    });
    const restoreCharts = boostChartResolution(doc);
    try {
      return await html2canvas(doc, { scale: EXPORT_SCALE, backgroundColor: '#fff' });
    } finally {
      restoreCharts();
      if (controls) controls.style.display = '';
      expandables.forEach((el, index) => {
        el.style.height = savedStyles[index].height;
        el.style.maxHeight = savedStyles[index].maxHeight;
        el.style.overflowY = savedStyles[index].overflowY;
      });
    }
  };

  const handleDownload = async (format) => {
    try {
      setIsLoading(true);
      const canvas = await captureCanvas();
      if (format === 'pdf') {
        const imgData = canvas.toDataURL('image/jpeg', PDF_IMAGE_QUALITY);
        const isLandscape = canvas.width >= canvas.height;
        const pdf = new jsPDF({ orientation: isLandscape ? 'l' : 'p', unit: 'pt', format: 'a4' });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgProps = pdf.getImageProperties(imgData);
        const scale = Math.min(
          (pageWidth - PDF_MARGIN * 2) / imgProps.width,
          (pageHeight - PDF_MARGIN * 2) / imgProps.height
        );
        const imgWidth = imgProps.width * scale;
        const imgHeight = imgProps.height * scale;
        pdf.addImage(
          imgData,
          'JPEG',
          (pageWidth - imgWidth) / 2,
          (pageHeight - imgHeight) / 2,
          imgWidth,
          imgHeight,
          undefined,
          'FAST'
        );
        pdf.save(`${reportName}.pdf`);
      } else {
        const image = canvas
          .toDataURL(`image/${format}`, 1.0)
          .replace(`image/${format}`, 'image/octet-stream');
        const anchor = document.createElement('a');
        document.body.appendChild(anchor);
        anchor.setAttribute('download', `${reportName}.${format}`);
        anchor.setAttribute('href', image);
        anchor.click();
        anchor.parentNode.removeChild(anchor);
      }
    } catch (e) {
      toast.error(t('Something went wrong'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dropdown>
      <Dropdown.Toggle
        variant="secondary"
        className="custom-dashboard-button btn-export-download"
        disabled={disabled || isLoading}
      >
        <HiDownload fontSize={17} />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item onClick={() => handleDownload('pdf')}>{t('PDF')}</Dropdown.Item>
        <Dropdown.Item onClick={() => handleDownload('png')}>{t('PNG')}</Dropdown.Item>
        <Dropdown.Item onClick={() => handleDownload('jpeg')}>{t('JPEG')}</Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default DashboardDownloadButton;
